import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Shield, Lock, CheckCircle, Globe, Briefcase, Phone, 
  Smartphone, Mail, User, Bitcoin, ArrowRight, Zap, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Type configurations
const typeConfig = {
  domain: { label: 'Website / Domain', icon: Globe },
  company: { label: 'Company', icon: Briefcase },
  phone: { label: 'Phone Number', icon: Phone },
  whatsapp: { label: 'WhatsApp Number', icon: Smartphone },
  email: { label: 'Email Address', icon: Mail },
  social: { label: 'Social Media Profile', icon: User },
  wallet: { label: 'Crypto Wallet', icon: Bitcoin }
};

const ScamCheckUnlock = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const entityType = searchParams.get('type') || 'domain';
  const entitySlug = searchParams.get('entity') || '';
  const originalQuery = searchParams.get('query') || entitySlug.replace(/-/g, ' ');

  const config = typeConfig[entityType] || typeConfig.domain;
  const TypeIcon = config.icon;

  const handlePayment = async (tier) => {
    setLoading(tier);
    try {
      // Standardized plan mapping - matches backend STRIPE_PRICE_ALLOWLIST
      // Note: UI uses 'instant'/'advanced' keys, mapping to backend plan names
      const planMap = {
        instant: 'scam_check_basic',      // £4.99 - Instant Scam Check
        advanced: 'scam_check_pro'        // £9.99 - Advanced Risk Analysis
      };

      const plan = planMap[tier];
      
  const response = await fetch('/api/stripe/create-checkout-session', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
  plan: plan,
  originUrl: window.location.origin,
  entity: entitySlug,
  entity_type: entityType,
  scan_token: `${entityType}_${entitySlug}_${Date.now()}`
})

});

      const data = await response.json();
      
      if (data.url) {
        // Store the entity info for after payment
        sessionStorage.setItem('scam_check_entity', JSON.stringify({
          type: entityType,
          slug: entitySlug,
          query: originalQuery
        }));
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Unable to process payment. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628] pt-28 pb-16">
      <Helmet>
        <title>Unlock Fraud Intelligence Scan | ActionFraudOne</title>
        <meta name="description" content="Unlock your fraud intelligence scan to view risk analysis results." />
      </Helmet>

      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          {/* Entity Being Checked */}
          <Card className="bg-[#0d1d32] border-[#1e40af]/30 mb-8">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#1e40af]/20 rounded-xl flex items-center justify-center">
                  <TypeIcon className="w-7 h-7 text-blue-400" />
                </div>
                <div className="flex-1">
                  <p className="text-gray-500 text-sm">{config.label} Check</p>
                  <p className="text-white text-xl font-semibold break-all">{originalQuery}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Unlock Message */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-[#1e40af]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-blue-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">
              Unlock Fraud Intelligence Scan
            </h1>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              To run this fraud intelligence scan and view the analysis results, please unlock the scan below.
            </p>
          </div>

          {/* Pricing Options */}
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {/* Basic Scan */}
            <Card className="bg-[#0d1d32] border-[#1e40af]/30 hover:border-[#1e40af]/50 transition-all">
              <CardContent className="p-8">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="w-6 h-6 text-blue-400" />
                  <h3 className="text-xl font-bold text-white">Instant Scam Check</h3>
                </div>
                
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-4xl font-bold text-blue-400">£4.99</span>
                  <span className="text-gray-500">one-time</span>
                </div>
                
                <p className="text-gray-500 mb-6">
                  Basic automated fraud risk scan with key indicators.
                </p>
                
                <ul className="space-y-3 mb-8">
                  {[
                    'Fraud Risk Score (0-100)',
                    'Basic Risk Indicators',
                    'AI Analysis Summary',
                    'Instant Results'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-400 text-sm">
                      <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  onClick={() => handlePayment('instant')}
                  disabled={loading === 'instant'}
                  className="w-full bg-[#1e40af] hover:bg-[#1e3a8a] text-white font-bold py-6"
                  data-testid="unlock-instant-btn"
                >
                  {loading === 'instant' ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    <>
                      <Lock className="mr-2 w-4 h-4" />
                      Unlock for £4.99
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Advanced Scan */}
            <Card className="bg-[#0d1d32] border-cyan-500/30 hover:border-cyan-500/50 transition-all relative">
              <div className="absolute -top-3 right-4 bg-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                <Star size={12} /> RECOMMENDED
              </div>
              <CardContent className="p-8">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-6 h-6 text-cyan-400" />
                  <h3 className="text-xl font-bold text-white">Advanced Risk Analysis</h3>
                </div>
                
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-4xl font-bold text-cyan-400">£9.99</span>
                  <span className="text-gray-500">one-time</span>
                </div>
                
                <p className="text-gray-500 mb-6">
                  Deeper fraud indicators and expanded intelligence analysis.
                </p>
                
                <ul className="space-y-3 mb-8">
                  {[
                    'Everything in Basic',
                    'Deep Fraud Indicators',
                    'Pattern Analysis',
                    'Related Scam Reports',
                    'Recommended Actions',
                    'Priority Support'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-400 text-sm">
                      <CheckCircle className="w-4 h-4 text-cyan-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  onClick={() => handlePayment('advanced')}
                  disabled={loading === 'advanced'}
                  className="w-full bg-gradient-to-r from-[#1e40af] to-cyan-500 hover:from-[#1e3a8a] hover:to-cyan-400 text-white font-bold py-6"
                  data-testid="unlock-advanced-btn"
                >
                  {loading === 'advanced' ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    <>
                      <Zap className="mr-2 w-4 h-4" />
                      Unlock for £9.99
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Trust Signals */}
          <div className="flex flex-wrap justify-center gap-6 text-gray-500 text-sm">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span>Secure Payment</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>Powered by Stripe</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span>Instant Results</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScamCheckUnlock;
